<template>
  <div class="attachments-modern">
    <b-overlay :show="loading" opacity="0.99">
      <!-- Upload Section -->
      <div class="upload-card mb-2">
        <div class="card-header-modern">
          <h3 class="card-title">
            <i class="fas fa-cloud-upload-alt mr-2"></i>
            Adicionar Anexo
          </h3>
        </div>
        <div class="card-content">
          <form @submit.prevent="createAttachment">
            <div class="upload-grid">
              <!-- File Input -->
              <div class="upload-area">
                <label
                  class="file-dropzone"
                  :class="{
                    'file-dropzone--has-file': selectedFiles.length > 0,
                  }"
                >
                  <input
                    id="attachment"
                    type="file"
                    class="file-input"
                    multiple
                    @change="handleFileSelect"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  <div class="dropzone-content">
                    <div class="dropzone-icon">
                      <i
                        :class="
                          selectedFiles.length > 0
                            ? 'fas fa-file-alt'
                            : 'fas fa-cloud-upload-alt'
                        "
                      ></i>
                    </div>
                    <div class="dropzone-text">
                      <span v-if="selectedFiles.length > 0" class="file-name">
                        {{ selectedFiles.length }} arquivo(s) selecionado(s)
                      </span>
                      <span v-else class="dropzone-label"
                        >Clique para selecionar o arquivo</span
                      >
                    </div>
                    <button
                      v-if="selectedFiles.length > 0"
                      type="button"
                      class="file-remove"
                      @click.stop="clearFile"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </label>
                <div class="file-info">
                  <p class="info-item">
                    <i class="fas fa-weight-hanging mr-1"></i>
                    Máximo <strong>10MB</strong>
                  </p>
                  <p class="info-item">
                    <i class="fas fa-file-image mr-1"></i>
                    Formatos: <strong>PDF, JPG, PNG</strong>
                  </p>
                </div>
              </div>

              <!-- Description -->
              <div class="description-area">
                <textarea
                  v-model="description"
                  rows="4"
                  class="form-textarea"
                  placeholder="Descreva o anexo (opcional)..."
                ></textarea>
              </div>
            </div>

            <!-- Errors -->
            <div v-if="errors.length > 0" class="errors-section">
              <ErrorsMessages :errors="errors" />
            </div>

            <!-- Submit Button -->
            <div class="upload-actions">
              <v-btn
                class="btn-save"
                type="submit"
                :disabled="loading || selectedFiles.length == 0"
              >
                <i class="fas fa-plus mr-2"></i>
                {{ loading ? "Enviando..." : "Adicionar Anexo" }}
              </v-btn>
            </div>
          </form>
        </div>
      </div>

      <!-- Attachments List -->
      <div class="attachments-card mb-2">
        <div class="card-header-modern">
          <h3 class="card-title">
            <i class="fas fa-paperclip mr-2"></i>
            Anexos
          </h3>
          <span class="attachments-count">
            {{ attachments.length }} arquivo{{
              attachments.length !== 1 ? "s" : ""
            }}
          </span>
        </div>
        <div class="card-content">
          <!-- Empty State -->
          <div v-if="attachments.length === 0" class="empty-state">
            <div class="empty-icon-container">
              <i class="fas fa-folder-open"></i>
            </div>
            <p class="empty-text">Nenhum anexo adicionado</p>
          </div>

          <!-- Attachments Grid -->
          <div v-else class="attachments-grid">
            <div
              v-for="(item, key) in attachments"
              :key="key"
              class="attachment-item"
            >
              <!-- File Icon -->
              <div class="attachment-icon" :class="getFileTypeClass(item.type)">
                <i :class="getFileIcon(item.type)"></i>
              </div>

              <!-- File Info -->
              <div class="attachment-info">
                <p class="attachment-description">
                  {{ item.description || "Sem descrição" }}
                </p>
                <div class="attachment-meta">
                  <span class="meta-item">
                    <i class="fas fa-file mr-1"></i>
                    {{ getFileTypeLabel(item.type) }}
                  </span>
                  <span class="meta-item">
                    <i class="fas fa-user mr-1"></i>
                    {{ formatUserName(item) }}
                  </span>
                </div>
              </div>

              <!-- Actions -->
              <div class="attachment-actions">
                <!-- PDF: Open in new tab -->
                <a
                  v-if="item.type === 'application/pdf'"
                  :href="item.attachment_url"
                  target="_blank"
                  class="action-btn action-btn--primary"
                >
                  <i class="fas fa-external-link-alt mr-1"></i>
                  Abrir
                </a>

                <!-- Image: Open modal -->
                <v-btn
                  v-else
                  x-small
                  type="button"
                  class="btn-modern btn-modern-info"
                  @click="$bvModal.show(`attachments_${key}`)"
                >
                  <i class="fas fa-eye mr-1"></i>
                  Visualizar
                </v-btn>

                <!-- Delete (only owner) -->
                <confirmation-dialog
                  v-if="item.user_id === user.id"
                  x_small
                  message-title="APAGAR ANEXO?"
                  message-body="Deseja realmente apagar este anexo? Esta ação não pode ser desfeita."
                  open-class="btn-modern btn-modern-delete"
                  open-left-icon="fa-trash"
                  open-text=""
                  @yes="deleteAttachment(item.id)"
                />

                <!-- Image Modal -->
                <b-modal
                  :id="`attachments_${key}`"
                  size="xl"
                  no-close-on-backdrop
                  hide-footer
                  centered
                >
                  <template #modal-header="{ close }">
                    <div class="modal-header-custom">
                      <h5 class="modal-title-custom">
                        <i class="fas fa-image mr-2"></i>
                        {{ item.description || "Visualizar Anexo" }}
                      </h5>
                      <button type="button" class="modal-close" @click="close">
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                  </template>
                  <div class="image-preview">
                    <img :src="item.attachment_url" :alt="item.description" />
                  </div>
                </b-modal>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import ErrorsMessages from "../../templates/Components/ErrorsMessages";
import ConfirmationDialog from "../../templates/Components/ConfirmationDialog";
import api from "../../services/api";
import toast from "../../lib/ToastNotification";
import { formatDate, professionalClassCouncilValue } from "../../lib/format";
import { userData } from "../../lib/functions";

export default {
  name: "Attachments",

  components: {
    ErrorsMessages,
    ConfirmationDialog,
  },

  props: {
    evolution_id: [String, Number],
  },

  data() {
    return {
      loading: false,
      user: null,
      description: "",
      errors: [],
      attachments: [],
      selectedFiles: [],
    };
  },

  created() {
    this.retrieve();
  },

  methods: {
    userData,
    formatDate,
    professionalClassCouncilValue,

    retrieve() {
      const { user } = userData();
      this.user = user;
      this.getAttachments();
    },

    handleFileSelect(event) {
      this.selectedFiles = Array.from(event.target.files);
      this.errors = [];
    },

    clearFile() {
      this.selectedFiles = [];
      document.getElementById("attachment").value = "";
    },

    getFileTypeClass(type) {
      return type === "application/pdf"
        ? "attachment-icon--pdf"
        : "attachment-icon--image";
    },

    getFileIcon(type) {
      return type === "application/pdf"
        ? "fas fa-file-pdf"
        : "fas fa-file-image";
    },

    getFileTypeLabel(type) {
      return type === "application/pdf" ? "PDF" : "Imagem";
    },

    formatUserName(item) {
      return this.professionalClassCouncilValue({
        id: item.user_professional_class_council_id,
        name: item.user_name,
        value: item.user_professional_class_council_value,
        cpf: item.user_cpf,
        office: item.user_office,
      });
    },

    async getAttachments() {
      try {
        this.loading = true;
        const { evolution_id } = this;

        const response = await api.get("/rest-home/evolution/attachments", {
          params: { evolution_id },
        });

        const { attachments } = response.data;
        this.attachments = attachments;
      } catch (error) {
        console.error("Erro ao carregar anexos:", error);
      } finally {
        this.loading = false;
      }
    },

    async createAttachment() {
      try {
        this.errors = [];

        if (!this.selectedFiles || !this.selectedFiles.length) {
          this.errors = [
            {
              id: this.errors.length,
              message: "Selecione ao menos um arquivo",
            },
          ];
          return;
        }

        const allowedMimes = [
          "image/jpg",
          "image/jpeg",
          "image/pjpeg",
          "image/png",
          "application/pdf",
        ];

        const limitSize = 10 * 1024 * 1024; // 10MB

        this.loading = true;

        for (const file of this.selectedFiles) {
          if (!allowedMimes.includes(file.type)) {
            this.errors = [
              {
                id: this.errors.length,
                message: `Formato de arquivo não permitido: ${file.name}`,
              },
            ];
            return;
          }

          if (file.size > limitSize) {
            this.errors = [
              {
                id: this.errors.length,
                message: `O arquivo ${file.name} excede o limite de 10MB`,
              },
            ];
            return;
          }

          const formData = new FormData();

          formData.append("evolution_id", this.evolution_id);
          formData.append("description", this.description);
          formData.append("attachment", file, file.name);
          formData.append("type", file.type);

          const response = await api.post(
            "/rest-home/evolution/attachment",
            formData
          );

          const { error, errors } = response.data;

          if (error) {
            this.errors = errors || [
              {
                id: this.errors.length,
                message: `Erro ao enviar ${file.name}`,
              },
            ];
            return;
          }
        }

        toast("Anexos enviados com sucesso", "success");

        this.description = "";
        this.selectedFiles = [];
        this.errors = [];

        const input = document.getElementById("attachment");
        if (input) {
          input.value = "";
        }

        await this.getAttachments();

        this.$emit("action");
      } catch (error) {
        console.error("Erro ao enviar anexo:", error);

        this.errors = [
          {
            id: this.errors.length,
            message: "Erro ao enviar arquivo",
          },
        ];
      } finally {
        this.loading = false;
      }
    },

    async deleteAttachment(id) {
      try {
        this.loading = true;

        const response = await api.delete("/rest-home/evolution/attachment", {
          data: { id },
        });

        const { error, message, errors } = response.data;

        if (!error) {
          toast(message, "success");
          this.getAttachments();
          this.errors = [];
          this.$emit("action");
        } else {
          this.errors = errors;
        }
      } catch (error) {
        console.error("Erro ao deletar anexo:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ===========================================
   LAYOUT PRINCIPAL
   =========================================== */

.attachments-modern {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* ===========================================
   CARDS (padrão do projeto)
   =========================================== */

.upload-card,
.attachments-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.card-header-modern {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.card-title i {
  color: #0f65b0;
}

.card-content {
  padding: 24px;
}

.attachments-count {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #93c5fd;
}

/* ===========================================
   UPLOAD AREA
   =========================================== */

.upload-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.upload-area {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.file-dropzone {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.file-dropzone:hover {
  border-color: #0f65b0;
  background: #eff6ff;
}

.file-dropzone--has-file {
  border-color: #0f65b0;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-style: solid;
}

.file-input {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.dropzone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
  text-align: center;
}

.dropzone-icon {
  width: 56px;
  height: 56px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #0f65b0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.file-dropzone--has-file .dropzone-icon {
  background: #0f65b0;
  color: white;
}

.dropzone-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

.file-name {
  font-size: 14px;
  color: #1e293b;
  font-weight: 600;
  word-break: break-all;
}

.file-remove {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.2s ease;
}

.file-remove:hover {
  background: #dc2626;
  transform: scale(1.1);
}

.file-info {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.info-item {
  font-size: 12px;
  color: #64748b;
  margin: 0;
  display: flex;
  align-items: center;
}

.info-item i {
  color: #94a3b8;
}

/* Description */
.description-area {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  display: flex;
  align-items: center;
}

.field-label i {
  color: #0f65b0;
}

.form-textarea {
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 14px;
  font-family: "WorkSans-Regular", sans-serif;
  background: white;
  transition: all 0.3s ease;
  resize: none;
  min-height: 120px;
}

.form-textarea:focus {
  outline: none;
  border-color: #0f65b0;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1);
}

/* Errors */
.errors-section {
  margin-top: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
  border-radius: 12px;
}

/* Actions */
.upload-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

/* ===========================================
   EMPTY STATE
   =========================================== */

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2px;
  text-align: center;
}

.empty-icon-container {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-icon-container i {
  font-size: 32px;
  color: #94a3b8;
}

.empty-text {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

/* ===========================================
   ATTACHMENTS GRID
   =========================================== */

.attachments-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.attachment-item:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

/* File Icon */
.attachment-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.attachment-icon--pdf {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
}

.attachment-icon--image {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
}

/* File Info */
.attachment-info {
  flex: 1;
  min-width: 0;
}

.attachment-description {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 6px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.attachment-meta {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.meta-item {
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
}

.meta-item i {
  color: #94a3b8;
  font-size: 11px;
}

/* Actions */
.attachment-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.action-btn--primary {
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  color: white;
}

.action-btn--primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(15, 101, 176, 0.3);
  color: white;
}

.action-btn--view {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  color: #475569;
  border: 1px solid #e5e7eb;
}

.action-btn--view:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.action-btn--delete {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #fca5a5;
  padding: 8px 12px;
}

.action-btn--delete:hover {
  background: #fecaca;
}

/* ===========================================
   MODAL
   =========================================== */

.modal-header-custom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 20px;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  color: white;
  margin: -1rem -1rem 0;
  border-radius: 0.3rem 0.3rem 0 0;
}

.modal-title-custom {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
}

.modal-close {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.image-preview {
  padding: 16px;
  background: #1e293b;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-preview img {
  max-width: 100%;
  max-height: 70vh;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

/* ===========================================
   RESPONSIVE
   =========================================== */

@media (max-width: 768px) {
  .upload-grid {
    grid-template-columns: 1fr;
  }

  .attachment-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .attachment-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .card-header-modern {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .file-info {
    flex-direction: column;
    gap: 4px;
  }
}

@media (max-width: 480px) {
  .card-content {
    padding: 16px;
  }

  .file-dropzone {
    min-height: 120px;
  }

  .dropzone-icon {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }

  .attachment-icon {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .action-btn {
    padding: 6px 10px;
    font-size: 11px;
  }
}
</style>